// ============================================
// CONFIGURAÇÃO CENTRALIZADA DE PREÇOS - UPCLINIC
// ============================================

export interface PlanFeature {
  name: string;
  included: boolean;
  description?: string;
}

export interface Plan {
  id: string;
  name: string;
  price: string;
  annualPrice?: string;
  originalPrice?: string;
  period: string;
  annualDiscount?: number;
  description: string;
  popular?: boolean;
  features: string[];
  detailedFeatures?: PlanFeature[];
  color?: string;
  savings?: string;
  cta?: string;
  monthlyPriceId?: string;
  annualPriceId?: string;
}

// PREÇOS OFICIAIS ATUALIZADOS 2025
export const CURRENT_PRICING = {
  STARTER: {
    MONTHLY: 29.90,
    ANNUAL: 299.00, // 10 meses pelo preço de 12
  },
  PROFESSIONAL: {
    MONTHLY: 59.90,
    ANNUAL: 599.00, // 10 meses pelo preço de 12
  },
  ENTERPRISE: {
    MONTHLY: 129.90,
    ANNUAL: 1299.00, // 10 meses pelo preço de 12
  }
};

// FORMATAÇÃO DE PREÇOS
export const formatPrice = (price: number): string => {
  return `R$ ${price.toFixed(2).replace('.', ',')}`;
};

// PLANOS PADRONIZADOS COM RECURSOS DETALHADOS
export const OFFICIAL_PLANS: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: formatPrice(CURRENT_PRICING.STARTER.MONTHLY),
    annualPrice: formatPrice(CURRENT_PRICING.STARTER.ANNUAL),
    period: '/mês',
    description: 'Ideal para consultórios pequenos',
    popular: false,
    color: '#22c55e',
    savings: 'Econômico',
    cta: 'Começar Grátis',
    monthlyPriceId: 'price_starter_monthly',
    annualPriceId: 'price_starter_annual',
    features: [
      'Até 10 pacientes',
      'Agenda para 1 profissional',
      'Prontuário eletrônico completo',
      'Telemedicina básica (50 consultas/mês)',
      'Relatórios essenciais',
      'IA Médica',
      'Suporte por email',
      'Backup automático',
      'Apps móveis básicos'
    ],
    detailedFeatures: [
      { name: 'Gestão de Pacientes', included: true, description: 'Até 10 pacientes cadastrados' },
      { name: 'Profissionais', included: true, description: '1 profissional na agenda' },
      { name: 'Prontuário Eletrônico', included: true, description: 'Sistema completo de prontuários' },
      { name: 'Telemedicina', included: true, description: '50 consultas online por mês' },
      { name: 'Relatórios', included: true, description: 'Relatórios básicos essenciais' },
      { name: 'Suporte', included: true, description: 'Suporte por email' },
      { name: 'IA Médica', included: true, description: 'Análises inteligentes incluídas' },
      { name: 'Farmácia', included: false, description: 'Integração farmacêutica não incluída' }
    ]
  },
  {
    id: 'professional',
    name: 'Professional',
    price: formatPrice(CURRENT_PRICING.PROFESSIONAL.MONTHLY),
    annualPrice: formatPrice(CURRENT_PRICING.PROFESSIONAL.ANNUAL),
    period: '/mês',
    description: 'Para clínicas em crescimento',
    popular: true,
    color: '#8b5cf6',
    savings: 'Mais Popular',
    cta: 'Mais Escolhido',
    monthlyPriceId: 'price_professional_monthly',
    annualPriceId: 'price_professional_annual',
    features: [
      'Pacientes ilimitados',
      'Até 5 profissionais',
      'Telemedicina avançada',
      'Relatórios avançados',
      'IA Médica integrada',
      'Suporte prioritário',
      'WhatsApp integrado',
      'Multi-especialidades'
    ],
    detailedFeatures: [
      { name: 'Gestão de Pacientes', included: true, description: 'Pacientes ilimitados' },
      { name: 'Profissionais', included: true, description: 'Até 5 profissionais' },
      { name: 'Prontuário Eletrônico', included: true, description: 'Sistema avançado de prontuários' },
      { name: 'Telemedicina', included: true, description: 'Telemedicina avançada ilimitada' },
      { name: 'Relatórios', included: true, description: 'Relatórios executivos avançados' },
      { name: 'IA Médica', included: true, description: 'IA médica completa integrada' },
      { name: 'Suporte', included: true, description: 'Suporte prioritário' },
      { name: 'Farmácia', included: true, description: 'Integração farmacêutica' }
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: formatPrice(CURRENT_PRICING.ENTERPRISE.MONTHLY),
    annualPrice: formatPrice(CURRENT_PRICING.ENTERPRISE.ANNUAL),
    period: '/mês',
    description: 'Para grandes clínicas e hospitais',
    popular: false,
    color: '#06b6d4',
    savings: 'Máximo Poder',
    cta: 'Falar com Vendas',
    monthlyPriceId: 'price_enterprise_monthly',
    annualPriceId: 'price_enterprise_annual',
    features: [
      'Pacientes ilimitados',
      'Profissionais ilimitados',
      'Prontuário eletrônico avançado',
      'Telemedicina avançada',
      'IA médica completa',
      'Relatórios executivos',
      'Suporte 24/7',
      'Gestor dedicado'
    ],
    detailedFeatures: [
      { name: 'Gestão de Pacientes', included: true, description: 'Pacientes ilimitados' },
      { name: 'Profissionais', included: true, description: 'Profissionais ilimitados' },
      { name: 'Prontuário Eletrônico', included: true, description: 'Prontuário eletrônico avançado' },
      { name: 'Telemedicina', included: true, description: 'Telemedicina avançada ilimitada' },
      { name: 'IA Médica', included: true, description: 'IA médica completa' },
      { name: 'Relatórios', included: true, description: 'Relatórios executivos' },
      { name: 'Suporte', included: true, description: 'Suporte 24/7' },
      { name: 'Gestor Dedicado', included: true, description: 'Gestor dedicado' }
    ]
  }
];

// PLANOS COM DESCONTO ANUAL
export const ANNUAL_PLANS: Plan[] = OFFICIAL_PLANS.map(plan => ({
  ...plan,
  price: formatPrice(CURRENT_PRICING[plan.id.toUpperCase() as keyof typeof CURRENT_PRICING].ANNUAL / 12),
  originalPrice: plan.price,
  period: '/mês*',
  savings: 'Economize 17%'
}));

// FUNÇÃO PARA OBTER PLANO POR ID
export const getPlanById = (id: string): Plan | undefined => {
  return OFFICIAL_PLANS.find(plan => plan.id === id);
};

// FUNÇÃO PARA CALCULAR ECONOMIA ANUAL
export const calculateAnnualSavings = (planId: string): number => {
  const plan = getPlanById(planId);
  if (!plan) return 0;
  
  const monthlyPrice = CURRENT_PRICING[planId.toUpperCase() as keyof typeof CURRENT_PRICING].MONTHLY;
  const annualPrice = CURRENT_PRICING[planId.toUpperCase() as keyof typeof CURRENT_PRICING].ANNUAL;
  
  return (monthlyPrice * 12) - annualPrice;
};

// CONFIGURAÇÕES DE TRIAL E PROMOÇÕES
export const TRIAL_CONFIG = {
  DAYS: 7,
  NO_CREDIT_CARD: true,
  CANCEL_ANYTIME: true
};

export const PROMOTIONS = {
  FIRST_MONTH_FREE: true,
  ANNUAL_DISCOUNT: 17, // Porcentagem
  BLACK_FRIDAY: false,
  STUDENT_DISCOUNT: false
};

// STRIPE PRICE IDS - CONFIGURAÇÃO REAL (Live)
// Produtos criados via Stripe MCP em 2026 (metadata source=landing_2026):
//   UpClinic Starter / Professional / Enterprise — BRL, recorrentes.
export const STRIPE_PRICE_IDS = {
  STARTER: {
    MONTHLY: 'price_1TqnHNJgDiwrjPstRA9t7vZe', // Starter Mensal (R$ 29,90)
    ANNUAL: 'price_1TqnHNJgDiwrjPstrIrNZB4E'   // Starter Anual (R$ 299,00)
  },
  PROFESSIONAL: {
    MONTHLY: 'price_1TqnHRJgDiwrjPstuJrktsiy', // Professional Mensal (R$ 59,90)
    ANNUAL: 'price_1TqnHUJgDiwrjPstBYyGkQeA'   // Professional Anual (R$ 599,00)
  },
  ENTERPRISE: {
    MONTHLY: 'price_1TqnHUJgDiwrjPstsmWrwA0v', // Enterprise Mensal (R$ 129,90)
    ANNUAL: 'price_1TqnHVJgDiwrjPstOMw7iZaN'   // Enterprise Anual (R$ 1.299,00)
  }
};

// Stripe Payment Links (modo subscription, Live) — usados como fallback e para fluxos
// sem sessão da Firebase Extension. Mesmos produtos/preços acima.
export const STRIPE_PAYMENT_LINKS = {
  STARTER: {
    MONTHLY: 'https://buy.stripe.com/eVq7sM2iad8Qe9U6URdnW00',
    ANNUAL: 'https://buy.stripe.com/fZucN66yq4Ck8PA92ZdnW01'
  },
  PROFESSIONAL: {
    MONTHLY: 'https://buy.stripe.com/4gM7sMf4W8SAfdYbb7dnW02',
    ANNUAL: 'https://buy.stripe.com/8x25kE5um7Ow1n8frndnW03'
  },
  ENTERPRISE: {
    MONTHLY: 'https://buy.stripe.com/eVqfZi6yq5Go7LwgvrdnW04',
    ANNUAL: 'https://buy.stripe.com/4gMcN6e0SfgYaXIgvrdnW05'
  }
};

/** Resolve live Payment Link for a plan + billing period. */
export const getStripePaymentLink = (
  planId: string,
  period: 'monthly' | 'annual' = 'monthly'
): string => {
  const planKey = planId.toUpperCase() as keyof typeof STRIPE_PAYMENT_LINKS;
  const periodKey = period.toUpperCase() as 'MONTHLY' | 'ANNUAL';
  const link = STRIPE_PAYMENT_LINKS[planKey]?.[periodKey];
  if (!link) {
    console.error(`❌ [PRICING] Payment Link não encontrado para: ${planId} - ${period}`);
    return STRIPE_PAYMENT_LINKS.PROFESSIONAL.MONTHLY;
  }
  return link;
};

// FUNÇÃO PARA OBTER PRICE ID DO STRIPE
export const getStripePriceId = (planId: string, period: 'monthly' | 'annual' = 'monthly'): string => {
  const planKey = planId.toUpperCase() as keyof typeof STRIPE_PRICE_IDS;
  const periodKey = period.toUpperCase() as keyof typeof STRIPE_PRICE_IDS[typeof planKey];
  
  if (!STRIPE_PRICE_IDS[planKey] || !STRIPE_PRICE_IDS[planKey][periodKey]) {
    console.error(`❌ [PRICING] Price ID não encontrado para: ${planId} - ${period}`);
    return STRIPE_PRICE_IDS.PROFESSIONAL.MONTHLY; // Fallback para plano mais popular
  }
  
  const priceId = STRIPE_PRICE_IDS[planKey][periodKey];
  console.log(`✅ [PRICING] Price ID encontrado: ${planId} ${period} = ${priceId}`);
  return priceId;
};

// ============================================
// CATÁLOGO PÚBLICO 2026 (Solo / Clínica-Studio / Clínica Plus)
// ============================================
// Mesmos preços, Price IDs e Payment Links do site clinicupapp.com/planos (conferidos nas
// páginas do Stripe em 14/10/2026). É o que a tela de Assinatura, o /checkout e o aviso de
// teste expirado mostram. Os IDs/links acima (Starter/Professional/Enterprise) continuam só
// para reconhecer assinaturas antigas (planLimits) — não oferecer em telas novas.
export type PublicPlanId = 'solo' | 'clinica' | 'plus';

export interface PublicPlan {
  id: PublicPlanId;
  name: string;
  tagline: string;
  accent: string;
  popular?: boolean;
  monthly: number;
  annual: number;
  priceIds: { monthly: string; annual: string };
  paymentLinks: { monthly: string; annual: string };
  features: string[];
}

export const PUBLIC_PLANS: PublicPlan[] = [
  {
    id: 'solo',
    name: 'Solo',
    tagline: '1 profissional — consultório solo de fisioterapia ou pilates',
    accent: '#22c55e',
    monthly: 59,
    annual: 590,
    priceIds: { monthly: 'price_1UN8MPJgDiwrjPst0smtTBZl', annual: 'price_1UN8MPJgDiwrjPstKCthdZgP' },
    paymentLinks: {
      monthly: 'https://buy.stripe.com/fZu5kE5um9WEc1M92ZdnW06',
      annual: 'https://buy.stripe.com/cNi3cw4qi1q83vgfrndnW07',
    },
    features: [
      'Agenda para 1 profissional',
      'Prontuário e evolução por sessão',
      'Lembretes no WhatsApp (sem cobrança por mensagem)',
      'Cobrança de mensalidade / financeiro básico',
      'Check-in Wellhub',
      'Suporte por e-mail',
    ],
  },
  {
    id: 'clinica',
    name: 'Clínica / Studio',
    tagline: 'Até 5 profissionais — clínicas e studios de pilates',
    accent: '#8b5cf6',
    popular: true,
    monthly: 129,
    annual: 1290,
    priceIds: { monthly: 'price_1UN8MQJgDiwrjPstxZ9yxnq3', annual: 'price_1UN8MRJgDiwrjPst7hGPyJQs' },
    paymentLinks: {
      monthly: 'https://buy.stripe.com/4gMbJ27Cu8SAd5Q1AxdnW08',
      annual: 'https://buy.stripe.com/28E14ocWO8SA4zk0wtdnW09',
    },
    features: [
      'Até 5 profissionais',
      'Pacientes ilimitados',
      'IA para apoio à evolução',
      'Cobrança recorrente / mensalidade',
      'WhatsApp sem cobrança por mensagem',
      'Check-in Wellhub',
      'Suporte prioritário',
    ],
  },
  {
    id: 'plus',
    name: 'Clínica Plus',
    tagline: 'Até 15 profissionais (+ R$ 15 por profissional extra)',
    accent: '#06b6d4',
    monthly: 249,
    annual: 2490,
    priceIds: { monthly: 'price_1UN8MSJgDiwrjPstCTZpyZS3', annual: 'price_1UN8MSJgDiwrjPstcISmdac7' },
    paymentLinks: {
      monthly: 'https://buy.stripe.com/cNi4gAe0S0m4gi2cfbdnW0a',
      annual: 'https://buy.stripe.com/8x228s1e6fgYd5Qbb7dnW0b',
    },
    features: [
      'Até 15 profissionais (+ R$ 15 por extra)',
      'Home Care com GPS',
      'IA completa + convênios',
      'WhatsApp sem cobrança por mensagem',
      'Gestor dedicado',
      'Suporte estendido',
    ],
  },
];

/** Plano público sugerido para um recurso bloqueado (Home Care/TISS/SNGPC só no Plus). */
export const publicPlanForFeature = (feature?: string | null): PublicPlanId =>
  feature === 'homecare' || feature === 'tiss' || feature === 'sngpc' ? 'plus' : 'clinica';